import React, { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { notification } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'

import TranslationNS from '@/utils/Constants/TranslationNS'
import { Constants } from '@/utils'
import { useAuth } from '@/middlewares/auth/auth-context'
import { getTimezoneForCountry } from '@/utils/countryTimezones'
import CountryInput from '@/components/common/CountryInput'
import TimezoneSelect from '@/components/common/TimezoneSelect'
import AddressAutoCompleteInput from '@/components/common/AddressAutoCompleteInput'
import EventHubDialog, { EVENT_HUB_DIALOG_Z } from '@/components/EventHub/EventHubDialog'
import { ORG_BTN_GHOST, ORG_BTN_PRIMARY, ORG_INPUT, ORG_NOTE } from '@/components/Organizer/shared/organizer.ui'
import SegmentedChoice from './SegmentedChoice'
import DateRangeField from './DateRangeField'
import { FunctionalIcon, GlobeIcon, HybridIcon, PinIcon, WorkshopIcon } from './icons'
import { CREATE_EVENT_FIELDS, isDuplicateNameError, submitCreateEvent, validateCreateEvent } from './index.utils'

import './index.css'

const FORM_ID = 'create-event-form'

const CATEGORY_ICONS = {
    'Functional Fitness': FunctionalIcon,
    'Hybrid Race': HybridIcon,
    Workshop: WorkshopIcon,
}

// Mapbox SearchBox skinned as a kit field (same variables as EventHubSettingsSidebar). The
// class names in `cssText` live in the Mapbox shadow root (search-js-web, flagged unstable):
// they only size the input to 33 / 44px, nothing breaks if they change.
const mapboxTheme = (invalid) => ({
    variables: {
        fontFamily: 'inherit',
        unit: '14px',
        borderRadius: '5px',
        colorBackground: '#f7f7f9',
        colorText: '#272727',
        border: `1px solid ${invalid ? '#b93a2c' : '#dbdadf'}`,
        boxShadow: 'none',
    },
    cssText: '.Input { height: 33px; } @media (max-width: 760px) { .Input { height: 44px; } }',
})
const MAPBOX_THEME = mapboxTheme(false)
const MAPBOX_THEME_INVALID = mapboxTheme(true)

/** One form row (mock `.fld-row`): label (+ red asterisk / ring only in error), control, 12px error. */
function FieldRow({ label, labelFor, labelId, error, errorId, note, noteId, children }) {
    const text = (
        <span>
            {label}{' '}
            <span className="req" aria-hidden="true">
                *
            </span>
        </span>
    )
    return (
        <div className={`cev-row${error ? ' err' : ''}`}>
            {labelFor ? (
                <label className="cev-lbl" htmlFor={labelFor}>
                    {text}
                </label>
            ) : (
                <span className="cev-lbl" id={labelId}>
                    {text}
                </span>
            )}
            {children}
            {note && (
                <p className={`${ORG_NOTE} cev-note`} id={noteId}>
                    {note}
                </p>
            )}
            {error && (
                <p className="cev-err" id={errorId}>
                    {error}
                </p>
            )}
        </div>
    )
}

/**
 * "Create an Event" modal (mock _kit/create.html, decision of 26 Sept 2026), opened in place on
 * any page by the CreateEventContext. Name → Type (Physical preselected) → Location (physical
 * only, the backend needs coordinates) → Category → Country + Time zone (set from the country
 * until the organizer touches the select) → one Start → End range field. Errors show all at
 * once under their field and the first one takes focus. On success the dialog closes and the
 * app lands on the organizer dashboard of the new event (`/{eventNumber}`).
 */
export default function CreateCompetition({ orgaId, onClose }) {
    const { t, i18n } = useTranslation(TranslationNS.MY_COMPETITIONS)
    const navigate = useNavigate()
    const { user } = useAuth()

    const [name, setName] = useState('')
    const [type, setType] = useState('inside')
    const [location, setLocation] = useState(null) // { location, longitude, latitude }
    const [category, setCategory] = useState('')
    const [country, setCountry] = useState('')
    const [timezone, setTimezone] = useState(user?.timezone || dayjs.tz.guess())
    const [timezoneTouched, setTimezoneTouched] = useState(false)
    const [dates, setDates] = useState({ start: '', end: '' })
    const [errors, setErrors] = useState({})
    const [isSubmitting, setIsSubmitting] = useState(false)

    const nameRef = useRef(null)
    const locationRef = useRef(null)
    const categoryRef = useRef(null)
    const countryRef = useRef(null)
    const startRef = useRef(null)
    const endRef = useRef(null)

    const clearError = (field) =>
        setErrors((previous) => {
            if (!previous[field]) return previous
            const next = { ...previous }
            delete next[field]
            return next
        })

    const focusField = (field) => {
        if (field === 'name') nameRef.current?.focus()
        else if (field === 'location') locationRef.current?.querySelector('mapbox-search-box, input')?.focus?.()
        else if (field === 'category') categoryRef.current?.focus()
        else if (field === 'country') countryRef.current?.querySelector('input')?.focus()
        else if (field === 'dates') (dates.start ? endRef : startRef).current?.focus()
    }

    const handleCountryChange = (value) => {
        setCountry(value)
        clearError('country')
        if (timezoneTouched) return
        const deduced = getTimezoneForCountry(value)
        if (deduced) setTimezone(deduced)
    }

    const handleSubmit = async (event) => {
        event.preventDefault()
        if (isSubmitting) return
        const values = { name, type, location, category, country, timezone, start: dates.start, end: dates.end }
        const nextErrors = validateCreateEvent(values, t)
        setErrors(nextErrors)
        const first = CREATE_EVENT_FIELDS.find((field) => nextErrors[field])
        if (first) {
            focusField(first)
            return
        }

        setIsSubmitting(true)
        try {
            const eventNumber = await submitCreateEvent(values, orgaId)
            onClose()
            navigate(`/${eventNumber}`)
        } catch (error) {
            console.error('/CreateCompetition - error', error)
            if (isDuplicateNameError(error)) {
                setErrors({ name: t('Event name already exists !') })
                nameRef.current?.focus()
            } else {
                notification.error({ message: t('Oops, an error occurred !') })
            }
            setIsSubmitting(false)
        }
    }

    // Escape with an open antd list (country, time zone) or inside the Mapbox box closes that
    // list only; the dialog's own Escape (EventHubDialog) runs otherwise.
    const handleKeyDown = (event) => {
        if (event.key !== 'Escape') return
        const target = event.target
        if (target.closest?.('.ant-select-open') || target.tagName?.toLowerCase().startsWith('mapbox-')) {
            event.stopPropagation()
        }
    }

    const typeOptions = [
        { value: 'inside', label: t('Physical'), Icon: PinIcon },
        { value: 'online', label: t('Online'), Icon: GlobeIcon },
    ]
    const categoryOptions = Constants.EVENT.CATEGORIES.map((value) => ({
        value,
        label: t(value),
        Icon: CATEGORY_ICONS[value],
    }))

    const footer = (
        <div className="cev-acts">
            <button type="button" className={`${ORG_BTN_GHOST} cev-ghost`} onClick={onClose} disabled={isSubmitting}>
                {t('Cancel')}
            </button>
            <button
                type="submit"
                form={FORM_ID}
                className={ORG_BTN_PRIMARY}
                disabled={isSubmitting}
                aria-busy={isSubmitting || undefined}
            >
                {isSubmitting ? (
                    <LoadingOutlined aria-hidden="true" />
                ) : (
                    <span className="cev-plus" aria-hidden="true" />
                )}
                {t('Create the event')}
            </button>
        </div>
    )

    return (
        <EventHubDialog
            id="create-event-dialog"
            title={t('Create an Event')}
            zClass={EVENT_HUB_DIALOG_Z.ALBUM}
            width="xl"
            closeSize="lg"
            bodyClassName="cev-body"
            footer={footer}
            initialFocusRef={nameRef}
            onClose={onClose}
        >
            <form id={FORM_ID} className="cev" noValidate onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
                <FieldRow label={t('Event name')} labelFor="cev-name" error={errors.name} errorId="cev-err-name">
                    <input
                        id="cev-name"
                        ref={nameRef}
                        type="text"
                        className={ORG_INPUT}
                        maxLength={80}
                        autoComplete="off"
                        placeholder={t('e.g. Summer Throwdown 2026')}
                        value={name}
                        onChange={(event) => {
                            setName(event.target.value)
                            if (event.target.value.trim()) clearError('name')
                        }}
                        aria-invalid={errors.name ? true : undefined}
                        aria-describedby={errors.name ? 'cev-err-name' : undefined}
                    />
                </FieldRow>

                <FieldRow label={t('Event type')} labelId="cev-type-l">
                    <SegmentedChoice
                        name="cev-type"
                        value={type}
                        options={typeOptions}
                        labelledBy="cev-type-l"
                        onChange={(value) => {
                            setType(value)
                            if (value !== 'inside') clearError('location')
                        }}
                    />
                </FieldRow>

                {type === 'inside' && (
                    <FieldRow
                        label={t('Location')}
                        labelId="cev-location-l"
                        error={errors.location}
                        errorId="cev-err-location"
                    >
                        <div ref={locationRef} role="group" aria-labelledby="cev-location-l">
                            <AddressAutoCompleteInput
                                onLocationChange={(data) => {
                                    setLocation(data)
                                    clearError('location')
                                }}
                                placeholder={t('Search an address or a venue')}
                                value={location?.location}
                                theme={errors.location ? MAPBOX_THEME_INVALID : MAPBOX_THEME}
                            />
                        </div>
                    </FieldRow>
                )}

                <FieldRow label={t('Category')} labelId="cev-cat-l" error={errors.category} errorId="cev-err-cat">
                    <SegmentedChoice
                        name="cev-cat"
                        stacked
                        value={category}
                        options={categoryOptions}
                        labelledBy="cev-cat-l"
                        describedBy={errors.category ? 'cev-err-cat' : undefined}
                        invalid={!!errors.category}
                        firstRef={categoryRef}
                        onChange={(value) => {
                            setCategory(value)
                            clearError('category')
                        }}
                    />
                </FieldRow>

                <div className="cev-2">
                    <FieldRow
                        label={t('Country')}
                        labelFor="cev-country"
                        error={errors.country}
                        errorId="cev-err-country"
                    >
                        <div ref={countryRef}>
                            <CountryInput
                                id="cev-country"
                                value={country || undefined}
                                onChange={handleCountryChange}
                                placeholder={t('Choose a country')}
                                alwaysOpen
                                aria-invalid={errors.country ? true : undefined}
                                aria-describedby={errors.country ? 'cev-err-country' : undefined}
                            />
                        </div>
                    </FieldRow>
                    <FieldRow
                        label={t('Time zone')}
                        labelFor="cev-tz"
                        note={t('Set from the country — you can change it.')}
                        noteId="cev-tz-note"
                    >
                        <TimezoneSelect
                            id="cev-tz"
                            selected={timezone}
                            onChange={(value) => {
                                if (!value) return
                                setTimezone(value)
                                setTimezoneTouched(true)
                            }}
                            allowClear={false}
                            aria-describedby="cev-tz-note"
                        />
                    </FieldRow>
                </div>

                <FieldRow label={t('Event dates')} labelId="cev-dates-l" error={errors.dates} errorId="cev-err-dates">
                    <DateRangeField
                        start={dates.start}
                        end={dates.end}
                        onChange={(next) => {
                            setDates(next)
                            // the error only lifts once both days are chosen (mock choose())
                            if (next.start && next.end) clearError('dates')
                        }}
                        invalid={!!errors.dates}
                        labelledBy="cev-dates-l"
                        describedBy={errors.dates ? 'cev-err-dates' : undefined}
                        startRef={startRef}
                        endRef={endRef}
                        language={i18n.resolvedLanguage}
                        t={t}
                    />
                </FieldRow>
            </form>
        </EventHubDialog>
    )
}
